import { useEffect, useRef, useState } from 'preact/hooks';

// ============================================================================
// Contact — the /contact page. Five addresses, one per kind of question, as
// cards a visitor can email or copy from. There's no form backend on
// purpose: every route ends in a plain mailto:, so nothing here can fail
// silently.

type TopicId = 'general' | 'sales' | 'support' | 'billing' | 'partnerships';

interface Topic {
  id: TopicId;
  email: string;
  title: string;
  purpose: string;
  blurb: string;
  subject: string;
  icon: string;
}

const TOPICS: Topic[] = [
  {
    id: 'general',
    email: 'hello@volgachat.com',
    title: 'Say hello',
    purpose: 'General enquiries',
    blurb: "General questions about VolgaChat, feedback, or anything that doesn't fit the other boxes.",
    subject: 'Question about VolgaChat',
    icon: '<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>',
  },
  {
    id: 'sales',
    email: 'sales@volgachat.com',
    title: 'Talk to sales',
    purpose: 'Sales & pricing',
    blurb: 'Interested in VolgaChat? Ask us about pricing, demos and product information.',
    subject: 'Pricing enquiry',
    icon: '<path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/>',
  },
  {
    id: 'support',
    email: 'support@volgachat.com',
    title: 'Get support',
    purpose: 'Customer support',
    blurb: 'Need help with your account, or experiencing a technical issue? We can help.',
    subject: 'Support request',
    icon: '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><path d="m4.93 4.93 4.24 4.24"/><path d="m14.83 9.17 4.24-4.24"/><path d="m14.83 14.83 4.24 4.24"/><path d="m9.17 14.83-4.24 4.24"/>',
  },
  {
    id: 'billing',
    email: 'billing@volgachat.com',
    title: 'Billing help',
    purpose: 'Billing & payments',
    blurb: 'Invoices, subscription payments, and any other billing-related questions.',
    subject: 'Billing question',
    icon: '<rect width="20" height="14" x="2" y="5" rx="2"/><path d="M2 10h20"/>',
  },
  {
    id: 'partnerships',
    email: 'partnerships@volgachat.com',
    title: 'Partner with us',
    purpose: 'Business partnerships',
    blurb: "Interested in partnerships, integrations, or collaboration opportunities? Let's build together.",
    subject: 'Partnership enquiry',
    icon: '<path d="m11 17 2 2a1 1 0 1 0 3-3"/><path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4"/><path d="m21 3 1 11h-2"/><path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3"/><path d="M3 4h8"/>',
  },
];

function mailto(email: string, subject: string): string {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}`;
}

function Icon({ paths, size = 22 }: { paths: string; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: paths }}
    />
  );
}

const COPY_ICON = '<rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>';
const CHECK_ICON = '<path d="M20 6 9 17l-5-5"/>';

/** Copies an address, falling back to a hidden textarea where the async
 * clipboard API isn't available (older browsers, non-secure local hosts). */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  }
}

function ContactCard({ topic, index }: { topic: Topic; index: number }) {
  const [copied, setCopied] = useState(false);
  // Each click drops a fresh ripple where the pointer landed; the counter
  // keys it so a second click restarts the animation instead of reusing it.
  const [ripple, setRipple] = useState<{ n: number; x: number; y: number } | null>(null);
  const cardRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);

  // A soft light that follows the cursor across the card.
  function onPointerMove(e: PointerEvent) {
    const el = cardRef.current;
    if (!el || e.pointerType !== 'mouse') return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--ct-x', `${e.clientX - r.left}px`);
    el.style.setProperty('--ct-y', `${e.clientY - r.top}px`);
  }

  return (
    <article
      ref={cardRef}
      class="ct-card"
      data-topic={topic.id}
      style={{ '--i': index }}
      onPointerMove={onPointerMove}
    >
      <span class="ct-card-icon">
        <Icon paths={topic.icon} />
      </span>
      <p class="ct-card-purpose">{topic.purpose}</p>
      <h3>{topic.title}</h3>
      <p class="ct-card-blurb">{topic.blurb}</p>
      <div class="ct-card-email">
        <a href={mailto(topic.email, topic.subject)} class="ct-card-address">
          {topic.email}
        </a>
      </div>
      <button
        type="button"
        class={`ct-copy${copied ? ' is-copied' : ''}`}
        onClick={async (e) => {
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          // A keyboard press has no pointer position, so it ripples from the middle.
          const fromPointer = e.detail > 0;
          setRipple({
            n: (ripple?.n ?? 0) + 1,
            x: fromPointer ? e.clientX - r.left : r.width / 2,
            y: fromPointer ? e.clientY - r.top : r.height / 2,
          });
          setCopied(await copyText(topic.email));
        }}
        aria-label={copied ? `Copied ${topic.email}` : `Copy ${topic.email}`}
      >
        {ripple && <span class="ct-copy-ripple" key={ripple.n} style={{ left: ripple.x, top: ripple.y }} aria-hidden="true" />}
        {/* Keyed so the icon and label swap with a little pop each way. */}
        <span class="ct-copy-face" key={copied ? 'done' : 'idle'}>
          <Icon paths={copied ? CHECK_ICON : COPY_ICON} size={16} />
          <span aria-live="polite">{copied ? 'Copied to clipboard' : 'Copy email address'}</span>
        </span>
      </button>
    </article>
  );
}

export function Contact() {
  const gridRef = useRef<HTMLDivElement>(null);

  // The cards rise in once, the first time they scroll into view — armed
  // only when that can actually fire, same as the home page's feature wall.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    grid.classList.add('is-armed');
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          grid.classList.add('is-in');
          io.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    io.observe(grid);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <section class="ct-hero">
        <span class="ct-blob ct-blob-a" aria-hidden="true" />
        <span class="ct-blob ct-blob-b" aria-hidden="true" />
        <span class="ct-blob ct-blob-c" aria-hidden="true" />
        <div class="site-wrap ct-hero-inner">
          <span class="site-eyebrow ct-eyebrow">
            <span class="ct-eyebrow-dot" aria-hidden="true" />
            Contact us
          </span>
          <h1>
            Let's talk. <span class="ct-gradient">We're here to help.</span>
          </h1>
          <p class="ct-lead">
            Whether you're comparing plans, stuck on an install, or have an idea to build together, pick the team that
            fits and drop us a line.
          </p>
        </div>
      </section>

      <section class="ct-section">
        <div class="site-wrap">
          <div class="ct-grid" ref={gridRef}>
            {TOPICS.map((t, i) => (
              <ContactCard key={t.id} topic={t} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section class="ct-section ct-section-alt">
        <div class="site-wrap">
          <aside class="ct-side" aria-label="Faster ways to get an answer">
            <h3>Want an answer right now?</h3>
            <ul>
              <li>
                <span class="ct-side-icon" aria-hidden="true">
                  <Icon paths={TOPICS[0].icon} size={18} />
                </span>
                <div>
                  <strong>Ask our assistant</strong>
                  <p>The chat bubble in the corner of this page runs on VolgaChat and knows the product inside out.</p>
                </div>
              </li>
              <li>
                <span class="ct-side-icon" aria-hidden="true">
                  <Icon paths='<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>' size={18} />
                </span>
                <div>
                  <strong>Read the FAQ</strong>
                  <p>
                    Trials, cancelling, and what happens to your data — <a href="/#faq">answered here</a>.
                  </p>
                </div>
              </li>
              <li>
                <span class="ct-side-icon" aria-hidden="true">
                  <Icon paths={TOPICS[1].icon} size={18} />
                </span>
                <div>
                  <strong>Compare plans</strong>
                  <p>
                    Every plan and price side by side on the <a href="/pricing">pricing page</a>.
                  </p>
                </div>
              </li>
            </ul>
          </aside>
        </div>
      </section>
    </>
  );
}
